import { KeyRoundIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { PERSONAL_ACCOUNT_COPY as COPY } from '../copy';
import { useOpenLinkedAccounts } from '../hooks/useOpenLinkedAccounts';
import { usePersonalAccountStanding } from '../hooks/usePersonalAccountStanding';
import { linkActionLabel, missingWords } from '../personalAccounts';

/**
 * A board in a workspace that requires each person's own account, seen by
 * someone without a usable one, says so before they move anything (spec 160
 * US5). Absent otherwise, and gone once they link: a notice that is always
 * there is a notice nobody reads.
 */
export function PersonalAccountNotice({
  workspaceId,
}: {
  workspaceId: string;
}) {
  const standing = usePersonalAccountStanding(workspaceId);
  const openLinkedAccounts = useOpenLinkedAccounts();
  const data = standing.data;
  if (!data?.required || data.missing.length === 0) return null;

  const hosts = data.missing
    .map((host) => `${host.host} (${missingWords(host.reason)})`)
    .join(' and ');

  return (
    <div
      // A status region, as the spend-limit notice beside it: announced when it
      // appears, named so it can be told from the board's other live regions.
      role="status"
      aria-label={COPY.noticeLabel}
      className="border-board-state-waiting/40 bg-board-state-waiting/10 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs"
    >
      <KeyRoundIcon
        aria-hidden="true"
        className="text-board-state-waiting size-4 shrink-0"
        strokeWidth={1.5}
      />
      <span className="text-foreground">{COPY.notice(hosts)}</span>
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0 text-xs"
        onClick={openLinkedAccounts}
      >
        {linkActionLabel(data.missing)}
      </Button>
    </div>
  );
}
